import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { getMediaRequested } from '../redux/reducers/media.slice';
import { getTierRequested } from '../redux/reducers/tier.slice';
import { getProjectRequested, updateProjectRequested } from '../redux/reducers/project.slice';

import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import queryString from 'query-string';

import { useMetaMask } from '../context/MetaMaskContext';
import { BigNumber, ethers } from 'ethers';

import MyToken1155Artifacts from '../artifacts/contracts/MyToken1155.sol/MyToken1155.json';
import { MyToken1155 } from '../@types/contracts/MyToken1155';

import Header from '../components/Header';
import Footer from '../components/Footer';
import { ReactComponent as ShareIconSVG } from '../svg/share-icon.svg';
import { ReactComponent as DeleteIconSVG } from '../svg/delete-icon.svg';
import ModalUpload from '../components/ModalUpload';
import ModalPublishedProject from '../components/ModalPublishedProject';
import Media from '../components/Media';
import Poster from '../components/Poster';
import CreateTier from '../components/CreateTier';

import CrewMemberAPIService from '../services/crewMember.service';
import MetadataAPIService from '../services/metadata.service';

import useToggle from '../hooks/useToggle';

const crewMemberAPIService = new CrewMemberAPIService();
const metadataAPIService = new MetadataAPIService();

function Home() {
  let { id: projectId } = useParams<{ id: string }>();

  const { loginWithMetaMask, metaMask } = useMetaMask();

  const medias = useAppSelector((state) => state.media.value);
  const tiers = useAppSelector((state) => state.tier.value);
  const project = useAppSelector((state) => state.project.current);
  const dispatch = useAppDispatch();

  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [isOpenCreateTier, toggleCreateTier] = useToggle(false);
  const [isOpenPublishedProject, setIsOpenPublishedProject] = useState(false);
  const [crewMembers, setCrewMembers] = useState<CrewMember[] | undefined>(project?.crewMembers);
  const [isFocusProjectNameInput, setIsFocusProjectNameInput] = useState<boolean>(false);
  const [isHoverProjectNameInput, setIsHoverProjectNameInput] = useState<boolean>(false);
  const [isFocusProjectDescriptionInput, setIsFocusProjectDescriptionInput] = useState<boolean>(false);
  const [isHoverProjectDescriptionInput, setIsHoverProjectDescriptionInput] = useState<boolean>(false);
  const [focusCrewMemberIndex, setFocusCrewMemberIndex] = useState<number>(-1);
  const [hoverCrewMemberIndex, setHoverCrewMemberIndex] = useState<number>(-1);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useForm({
    // defaultValues: {
    //   projectName: project?.name,
    //   projectDescription: project?.description,
    //   quantity: null,
    //   name: null,
    // },
  });

  useLayoutEffect(() => {
    if (!projectId) return;

    dispatch(getProjectRequested({ id: +projectId }));
    dispatch(getMediaRequested({ projectId: +projectId }));
    dispatch(getTierRequested({ projectId: +projectId }));
  }, [projectId]);

  useLayoutEffect(() => {
    if (!project) return;

    setCrewMembers(project.crewMembers);
  }, [project]);

  useEffect(() => {
    if (!metaMask.isConnected || !metaMask.addressNFT1155) {
      return;
    }

    const provider = new ethers.providers.Web3Provider(metaMask.provider);
    const contract = new ethers.Contract(metaMask.addressNFT1155, MyToken1155Artifacts.abi, provider) as MyToken1155;

    contract.on('TransferSingle', (operator, from, to, id, value) => {
      console.log(operator, from, to, id, value);
      const tokenId = BigNumber.from(id).toNumber();
      const quantity = BigNumber.from(value).toNumber();

      window.gtag('event', 'mint_NFT', {
        tokenId: tokenId,
        quantity: quantity,
      });

      setIsOpenPublishedProject(true);

      toast(`Minted: tokenId: ${tokenId} --- quantity ${quantity}`);
    });

    return () => {
      contract.removeAllListeners('TransferSingle');
    };
  }, [metaMask]);

  useLayoutEffect(() => {
    if (!project) return;

    setValue('projectName', getValues('projectName') || project.name, { shouldValidate: true });
    setValue('projectDescription', getValues('projectDescription') || project.description, { shouldValidate: true });
  }, [project]);

  function toggleModalUpload() {
    setIsOpenUpload((prev) => !prev);
  }

  async function onMintNftToke1155() {
    if (!metaMask.isConnected) {
      toast('Connect your wallet to start');
      return;
    }
    if (!project?.fileName) {
      toast('Poster is required');
      return;
    }

    if (!metaMask.addressNFT1155) {
      toast(`This chain is not supported`);
      return;
    }

    if (project?.type === 'classic' && medias.length === 0) {
      toast(`Can't publish with project type as classic without media`);
      return;
    }

    try {
      const provider = new ethers.providers.Web3Provider(metaMask.provider);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(metaMask.addressNFT1155, MyToken1155Artifacts.abi, signer) as MyToken1155;

      if (!metaMask.account) {
        await loginWithMetaMask();
        return;
      }

      const tierMediaMapping = tiers.map((item) => {
        return {
          tierId: item.id,
          mediaIds: item.medias?.map((item) => item.id),
        };
      });

      toast.loading(`Creating metadata...`, {
        duration: 500,
      });

      let res = await metadataAPIService.createMetadata(tierMediaMapping);

      console.log('res', res);

      res = res.map((item) => {
        item.price = ethers.utils.parseUnits(`${item.price}`, 'ether');
        return item;
      });

      console.log(res);

      toast.success(`Created metadata...`);

      const transaction = await contract.mintOrUpdateNFT(res);

      toast.promise(transaction.wait(), {
        loading: `Transaction submitted. Wait for confirmation...`,
        success: <b>Transaction confirmed!</b>,
        error: <b>Transaction failed!.</b>,
      });

      // refetch total token after processing
      transaction
        .wait()
        .then((res: any) => {
          console.log({ res });
          setTimeout(() => {
            saveProject();
            dispatch(getTierRequested({ projectId: +projectId }));
          }, 5000);
        })
        .catch((error) => console.error(error));
    } catch (error) {
      console.error(error);
    }
  }

  function saveProject() {
    const name = getValues('projectName');
    const description = getValues('projectDescription');

    const newProject = {
      ...project,
      name,
      description,
    } as Project;

    dispatch(updateProjectRequested({ project: newProject }));
  }

  const changeCrewMember = (newRoleName: string, newPercentage: number, crewMember: CrewMember) => {
    if (!crewMembers || !project) {
      return;
    }

    const idx = crewMembers?.findIndex((item) => item.id === crewMember.id);
    let newCrewMember = { ...crewMember };

    if (idx === -1) {
      return;
    }

    newCrewMember.percentage = newPercentage;
    newCrewMember.roleName = newRoleName;

    const newCrewMembers = [...crewMembers];
    newCrewMembers[idx] = newCrewMember;

    setCrewMembers(newCrewMembers);
  };

  const updateCrewMember = async (newRoleName: string, newPercentage: number, crewMember: CrewMember) => {
    if (!crewMembers || !project) {
      return;
    }

    const idx = crewMembers?.findIndex((item) => item.id === crewMember.id);

    let newCrewMember = { ...crewMember };
    const newCrewMembers = [...crewMembers];

    if (idx === -1) {
      return;
    }

    newCrewMember.percentage = newPercentage;
    newCrewMember.roleName = newRoleName;

    const otherMemberPercentage = newCrewMembers
      .filter((_, index) => index !== idx)
      .reduce((sum, c) => sum + c.percentage, 0);

    if (otherMemberPercentage + newPercentage > 100) {
      newCrewMember.percentage = 100 - otherMemberPercentage;
    }

    newCrewMembers[idx] = newCrewMember;
    setCrewMembers(newCrewMembers);

    await crewMemberAPIService.updateCrewMember(project, newCrewMember);
  };

  const renderMedia = useMemo(() => {
    return medias.map((media: Media, index) => <Media key={media?.id || index} media={media} />);
  }, [medias]);

  function copyCrewMemberLink(index: number) {
    if (!crewMembers) return;

    const data = {
      role_name: crewMembers[index].roleName,
      crew_id: crewMembers[index].id,
    };

    const mediaURL = `${location.origin}/projects/${projectId}?${queryString.stringify(data)}`;

    copy(mediaURL);
    toast.success('Copied');
  }

  const createNewCrewMember = async () => {
    if (!crewMembers || !project) return;

    const newCrewMembers = [...crewMembers];

    let newCrewMember = {
      projectId: +projectId,
      roleName: 'New crew member',
      percentage: 0,
    };

    newCrewMember = await crewMemberAPIService.createCrewMember(project, newCrewMember);
    newCrewMembers.push(newCrewMember);

    setCrewMembers(newCrewMembers);
    setFocusCrewMemberIndex(newCrewMembers.length - 1);
  };

  const deleteCrewMember = async (crewMember: CrewMember) => {
    if (!crewMembers || !project) return;

    const newCrewMembers = crewMembers.filter((item) => item.id !== crewMember.id);

    await crewMemberAPIService.deleteCrewMember(crewMember);

    setCrewMembers(newCrewMembers);
  };

  return (
    <>
      <Header />

      <div className="row-1">
        <React.Fragment>
          <Poster />
        </React.Fragment>

        <section className="project-info">
          <div className="project-form">
            <div className="app-input">
              <label>
                <strong>Project name</strong>
              </label>
              <input
                {...register('projectName')}
                onBlur={() => setIsFocusProjectNameInput(false)}
                onFocus={() => setIsFocusProjectNameInput(true)}
                onMouseOver={() => setIsHoverProjectNameInput(true)}
                onMouseOut={() => setIsHoverProjectNameInput(false)}
                type="text"
                autoComplete="off"
              />
              <div
                style={{
                  opacity: Number(isFocusProjectNameInput && isHoverProjectNameInput),
                }}
                className="app-input--hint"
              >
                max 100 characters
              </div>
            </div>
            <div style={{ height: 41 }}></div>
            <div className="app-input">
              <label>
                <strong>Description</strong>
              </label>
              <textarea
                style={{ height: 120 }}
                {...register('projectDescription')}
                onBlur={() => setIsFocusProjectDescriptionInput(false)}
                onFocus={() => setIsFocusProjectDescriptionInput(true)}
                onMouseOver={() => setIsHoverProjectDescriptionInput(true)}
                onMouseOut={() => setIsHoverProjectDescriptionInput(false)}
                autoComplete="off"
              />
              <div
                style={{
                  opacity: Number(isFocusProjectDescriptionInput && isHoverProjectDescriptionInput),
                }}
                className="app-input--hint"
              >
                max 500 characters
              </div>
            </div>
          </div>

          {project?.type === 'classic' && (
            <div className="crew-info">
              <div className="crew-info--header">
                <div>Crew</div>
                <div style={{ marginLeft: 8 }}>
                  <button type="button" className="app-btn color-primary icon" onClick={createNewCrewMember}>
                    +
                  </button>
                </div>
              </div>

              <div className="crew-info--body">
                <div className="crew-member">
                  {crewMembers?.map((item, index) => (
                    <div
                      key={index}
                      className="crew-member--item"
                      onClick={() => {
                        setHoverCrewMemberIndex(-1);
                      }}
                      onMouseEnter={() => focusCrewMemberIndex !== index && setHoverCrewMemberIndex(index)}
                      onMouseLeave={() => {
                        setHoverCrewMemberIndex(-1);
                      }}
                      onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget)) {
                          setFocusCrewMemberIndex(-1);
                        }
                      }}
                      style={
                        hoverCrewMemberIndex === index
                          ? {
                              background: '#464A4D',
                              borderRadius: 5,
                              cursor: 'pointer',
                            }
                          : {}
                      }
                    >
                      <div className="crew-member--item-name" onClick={() => setFocusCrewMemberIndex(index)}>
                        {focusCrewMemberIndex === index ? (
                          <input
                            value={item.roleName}
                            onChange={(e) => changeCrewMember(e.target.value, item.percentage, item)}
                            onBlur={(event) => {
                              updateCrewMember(event.target.value, item.percentage, item);
                              if (!event.currentTarget.contains(event.relatedTarget)) {
                                setFocusCrewMemberIndex(-1);
                              }
                            }}
                          ></input>
                        ) : (
                          <input
                            value={item.roleName}
                            readOnly
                            style={{
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          ></input>
                        )}
                      </div>
                      <div
                        className="crew-member--item-percentage"
                        onClick={() => setFocusCrewMemberIndex(index + 0.5)}
                      >
                        {focusCrewMemberIndex === index + 0.5 ? (
                          <>
                            <input
                              value={item.percentage}
                              onChange={(e) =>
                                changeCrewMember(item.roleName, Number(e.target.value.replace(/[^0-9]/g, '')), item)
                              }
                              onBlur={(event) => {
                                updateCrewMember(
                                  item.roleName,
                                  Number(event.target.value.replace(/[^0-9]/g, '')),
                                  item,
                                );
                                if (!event.currentTarget.contains(event.relatedTarget)) {
                                  setFocusCrewMemberIndex(-1);
                                }
                              }}
                            ></input>
                            <span>%</span>
                          </>
                        ) : (
                          <>
                            <input
                              value={item.percentage}
                              onChange={(e) =>
                                updateCrewMember(item.roleName, Number(e.target.value.replace(/[^0-9]/g, '')), item)
                              }
                              style={{
                                border: 'none',
                                cursor: 'pointer',
                              }}
                            ></input>
                            <span>%</span>
                          </>
                        )}
                      </div>
                      <span className="crew-member--item-icon" onClick={() => copyCrewMemberLink(index)}>
                        <ShareIconSVG />
                      </span>
                      <span className="crew-member--item-icon" onClick={() => deleteCrewMember(item)}>
                        <DeleteIconSVG />
                      </span>
                    </div>
                  ))}
                </div>
                <div className="crew-total">
                  <div className="crew-total--item">
                    <div>Total:</div>
                    <div>
                      {crewMembers?.map((item) => item.percentage)?.reduce((a, b) => a + b, 0)}
                      <span>%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="row-2">
        <div className="management-nft">
          <div className="management-nft--header">
            <div>NFT tier</div>
            <div style={{ marginLeft: 8 }}>
              <button type="button" className="app-btn color-primary icon" onClick={() => toggleCreateTier()}>
                +
              </button>
            </div>
          </div>
          <div className="management-nft--body">
            {isOpenCreateTier && project && <CreateTier project={project} toggleCreateTier={toggleCreateTier} />}
            <div className="nft-tier">
              {tiers?.map((item: Tier, index) => (
                <div key={item.id || index} className="nft-tier--item">
                  <div className="nft-tier--item-img">
                    <img src={`https://xyz-project-bucket.s3.us-west-2.amazonaws.com/${item?.fileName}`} />
                  </div>
                  <div className="nft-tier--item-info">
                    <div>{item.name}</div>
                    <div>Quantity: {item.quantity.toLocaleString()}</div>
                    <div>
                      Price: {item.price} {metaMask.chainToken}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="management-media">
          <div className="management-media--header">
            <div>Add media</div>
            <div style={{ marginLeft: 8 }}>
              <button type="button" className="app-btn color-primary icon" onClick={toggleModalUpload}>
                +
              </button>
            </div>
          </div>
          <div className="management-media--body">
            <React.Fragment>{renderMedia}</React.Fragment>
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right', margin: '32px 32px 72px 32px' }}>
        <button style={{ marginRight: 8 }} className="app-btn " type="button" onClick={saveProject}>
          Save
        </button>

        <button className="app-btn color-primary" type="button" onClick={onMintNftToke1155}>
          Publish
        </button>
      </div>
      <Footer />
      {isOpenUpload && <ModalUpload isOpen={isOpenUpload} toggle={toggleModalUpload} />}
      <ModalPublishedProject
        isOpen={isOpenPublishedProject}
        toggle={() => setIsOpenPublishedProject(!isOpenPublishedProject)}
        projectId={Number(project?.id)}
      />
    </>
  );
}

export default Home;
